import React from 'react';

import styled from '@emotion/styled';

import JobsIcon from '../../../components/Icons/JobsIcon';
import { salaryInterval, truncateTextByCharacters } from '../../../utils/utils';
import RichTextDisplay from '../../../components/RichTextDisplay/RichTextDisplay';
import { truncateText } from '../../../utils/utils';
import { useAppSelector } from '../../../redux/store';
import PostJobModal from '../../../components/Modals/ModalsActions/PostJobModal';
import { Modal } from '../../../components/Modals/Modal';
import { ModalProvider } from '../../../context/ModalContext';
import ModalTriggerContainer from '../../../components/Modals/ModalTriggerContainer';
import useJobPermissions from '../../../hooks/dashboard/jobs/useJobPermissions';
import Img from '../../../components/Img/Img';

interface ManagePostJobHeaderProps {
  title: string;
  jobDescription: string;
  yearsOfExperience: string;
  pay: number;
  payInterval: string;
  id: string;
}

const ManagePostJobHeader: React.FC<ManagePostJobHeaderProps> = ({
  title,
  jobDescription,
  yearsOfExperience,
  pay,
  payInterval,
  id,
}) => {
  useJobPermissions(id, true);

  // Use your Redux selector to find the permission for this job.
  const jobPermission = useAppSelector((state) =>
    state.jobPermissions.permissions.find((perm) => perm.jobId === id)
  );

  const EditJobButton = ({ children }: { children: React.ReactNode }) => {
    return (
      <ModalProvider>
        <ModalTriggerContainer>{children}</ModalTriggerContainer>
        <Modal>
          <PostJobModal jobId={id} />
        </Modal>
      </ModalProvider>
    );
  };

  return (
    <CenterContent>
      <CenterContentHeader>
        <Div>
          <ImageContainer>
            <Img
              // src={imgSrc}
              src={'/static/img/company-placeholder.png'}
              alt="shift location image"
            />
          </ImageContainer>
          <CenterContentText>{title}</CenterContentText>
          <EditJobButton>
            <EditJob disabled={!jobPermission?.canEdit}>edit job</EditJob>
          </EditJobButton>
        </Div>
        <Description>
          <RichTextDisplay
            richTextContent={
              jobDescription
                ? (() => {
                    const first20 = jobDescription.slice(0, 20);
                    const spaceCount = (first20.match(/ /g) || []).length;
                    return spaceCount < 4
                      ? truncateTextByCharacters(jobDescription, 30)
                      : truncateText(jobDescription);
                  })()
                : ''
            }
          />
        </Description>
      </CenterContentHeader>

      <CenterContentLowerPart>
        <CenterContentFooter>
          <IconWrapper>
            <JobsIcon />
            {yearsOfExperience} year+
          </IconWrapper>

          <div>
            {pay.formatCurrency()}/{salaryInterval[payInterval] ?? ''}
          </div>
        </CenterContentFooter>
      </CenterContentLowerPart>
    </CenterContent>
  );
};

export default ManagePostJobHeader;

const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
  /* border-bottom: 1px solid red; */
  margin-left: -32px;
  margin-right: -32px;
  padding-left: 40px;
  padding-right: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey4};
  /* border-bottom: #; */

  @media (max-width: 540px) {
    padding-left: 0px;
    padding-right: 0px;
    margin-left: 0;
    margin-right: 0;
  }
`;

const CenterContentHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CenterContentText = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.blackBlack2};

  @media (max-width: 540px) {
    font-size: 18px;
    margin-right: auto;
  }
`;

const Description = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.greyGrey1};

  @media (max-width: 540px) {
    font-size: 14px;
  }
`;

const CenterContentFooter = styled.div`
  display: flex;
  gap: 24px;
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 540px) {
    font-size: 12px;
  }
`;

const CenterContentLowerPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const IconWrapper = styled.div`
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 4px;
  align-items: center;

  & > svg {
    width: 14px;
    height: 14px;
  }

  @media (max-width: 540px) {
    font-size: 12px;
  }
`;

const EditJob = styled.button`
  width: fit-content;
  margin-top: 8px;
  color: ${({ theme }) => theme.palette.mainBlue};
  background: none;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    color: ${({ theme }) => theme.palette.greyGrey1};
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 540px) {
    font-size: 12px;
  }
`;

const Div = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 540px) {
    justify-content: space-between;
  }
`;

const ImageContainer = styled.div`
  width: 100px;
  height: 100px;

  @media (max-width: 540px) {
    width: 50px;
    height: 50px;
  }
`;
